import { Fragment, type ReactNode, type RefObject } from 'react';

import { Loader2 } from 'lucide-react';

import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@nesso/ui/primitives/command';
import { Popover, PopoverAnchor, PopoverContent } from '@nesso/ui/primitives/popover';

import { useOptionsGroups } from '@/shared/lib/use-options-groups';

import type { MultiSelectOption } from './multi-select-option';
import { MultiSelectItem } from './multi-select-popover/multi-select-item';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    fieldRef: RefObject<HTMLElement | null>;
    options: MultiSelectOption[];
    selected: string[];
    onChange: (selected: string[]) => void;
    maxSelected?: number;
    groupOrder?: string[];
    searchPlaceholder?: string;
    notFoundMessage: string;
    loading: boolean;
    children: ReactNode;
};

export function MultiSelectPopover({
    open,
    onOpenChange,
    fieldRef,
    options,
    selected,
    onChange,
    maxSelected,
    groupOrder,
    searchPlaceholder,
    notFoundMessage,
    loading,
    children,
}: Props) {
    const { ungrouped, groups } = useOptionsGroups(options, groupOrder);

    const selectedSet = new Set(selected);
    const isCapped = !!maxSelected && selected.length >= maxSelected;

    const handleToggle = (value: string) => {
        if (selected.includes(value)) {
            onChange(selected.filter((v) => v !== value));
        } else {
            if (maxSelected && selected.length >= maxSelected) return;
            onChange([...selected, value]);
        }
    };

    const renderItem = (option: MultiSelectOption) => {
        const isSelected = selectedSet.has(option.value);
        return (
            <MultiSelectItem
                key={option.value}
                value={option.value}
                label={option.label}
                isSelected={isSelected}
                isDisabled={isCapped && !isSelected}
                onToggle={handleToggle}
            />
        );
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange} modal={true}>
            <PopoverAnchor asChild>{children}</PopoverAnchor>
            <PopoverContent
                className="p-0"
                style={{
                    width: 'var(--radix-popover-trigger-width)',
                }}
                align="start"
                side="bottom"
                onCloseAutoFocus={(e) => {
                    e.preventDefault();
                }}
                onInteractOutside={(e) => {
                    // fixes double click on the trigger
                    const target = e.target as Node | null;
                    if (fieldRef.current && target && fieldRef.current.contains(target)) {
                        e.preventDefault();
                    }
                }}
            >
                <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList className="max-h-50">
                        <CommandEmpty>{notFoundMessage}</CommandEmpty>
                        {loading ? (
                            <CommandGroup>
                                <CommandItem className="justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                </CommandItem>
                            </CommandGroup>
                        ) : (
                            <>
                                {groups.map(([name, items], index) => (
                                    <Fragment key={name}>
                                        {index > 0 && <CommandSeparator />}
                                        <CommandGroup heading={name}>
                                            {items.map(renderItem)}
                                        </CommandGroup>
                                    </Fragment>
                                ))}
                                {ungrouped.length > 0 && (
                                    <>
                                        {groups.length > 0 && <CommandSeparator />}
                                        <CommandGroup>{ungrouped.map(renderItem)}</CommandGroup>
                                    </>
                                )}
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
